<template>
  <DashboardContainer pageTitle="Notifications">
    <GivarooSpinner v-if="loading && !notifications?.list?.length" class="mt-24" />
    <template v-else>
      <div class="campaign-data-shell">
        <section class="campaign-data-toolbar">
          <div class="flex flex-wrap items-center gap-2">
            <BaseButton
              variant="secondary"
              :icon="ArrowPathIcon"
              :loading="refreshing"
              @click="refreshNotifications"
            >
              Refresh
            </BaseButton>
          </div>
          <p class="text-sm text-gray-500 sm:max-w-xs sm:text-right">
            <span class="font-medium text-gray-700">{{ totalCount }}</span>
            {{ totalCount === 1 ? 'notification' : 'notifications' }} total
          </p>
        </section>

        <div class="campaign-data-stats">
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Unread</p>
            <p class="campaign-data-stat-card__value">{{ unreadCount }}</p>
          </div>
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Read</p>
            <p class="campaign-data-stat-card__value">{{ readCount }}</p>
          </div>
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Donations</p>
            <p class="campaign-data-stat-card__value">{{ donationCount }}</p>
          </div>
          <div class="campaign-data-stat-card">
            <p class="campaign-data-stat-card__label">Tasks</p>
            <p class="campaign-data-stat-card__value">{{ taskCount }}</p>
          </div>
        </div>

        <nav class="campaign-data-tabs" aria-label="Notification filter">
          <button
            type="button"
            class="campaign-data-tab"
            :class="{ 'campaign-data-tab--active': activeTab === 'all' }"
            @click="activeTab = 'all'"
          >
            <BellIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            All
            <span class="campaign-data-tab-badge">{{ listCount }}</span>
          </button>
          <button
            type="button"
            class="campaign-data-tab"
            :class="{ 'campaign-data-tab--active': activeTab === 'unread' }"
            @click="activeTab = 'unread'"
          >
            <InboxIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            Unread
            <span class="campaign-data-tab-badge">{{ unreadCount }}</span>
          </button>
          <button
            type="button"
            class="campaign-data-tab"
            :class="{ 'campaign-data-tab--active': activeTab === 'read' }"
            @click="activeTab = 'read'"
          >
            <CheckCircleIcon class="h-4 w-4 shrink-0" aria-hidden="true" />
            Read
            <span class="campaign-data-tab-badge">{{ readCount }}</span>
          </button>
        </nav>

        <section class="campaign-data-workspace">
          <div v-if="!notifications?.list?.length" class="py-12 text-center">
            <div
              class="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400"
            >
              <BellIcon class="h-7 w-7" aria-hidden="true" />
            </div>
            <h3 class="mt-4 text-base font-semibold text-gray-900">No notifications yet</h3>
            <p class="mt-1 text-sm text-gray-500">
              You'll see donation and task notifications here when they arrive.
            </p>
          </div>

          <div v-else-if="!displayedNotifications.length" class="py-12 text-center">
            <p class="text-sm text-gray-500">
              {{ emptyTabMessage }}
            </p>
          </div>

          <div v-else class="space-y-3">
            <article
              v-for="notification in displayedNotifications"
              :key="notification.id"
              :class="[
                'notifications-page-item',
                { 'notifications-page-item--unread': !notification.readStatus },
              ]"
              @click="toggleReadStatus(notification)"
            >
              <div class="flex items-start gap-3">
                <div
                  :class="[
                    'app-navbar-notifications__icon',
                    notification.readStatus
                      ? 'app-navbar-notifications__icon--read'
                      : 'app-navbar-notifications__icon--unread',
                  ]"
                >
                  <CurrencyDollarIcon
                    v-if="isDonationNotification(notification)"
                    class="h-4 w-4"
                    aria-hidden="true"
                  />
                  <ClipboardDocumentListIcon
                    v-else-if="isTaskNotification(notification)"
                    class="h-4 w-4"
                    aria-hidden="true"
                  />
                  <BellIcon v-else class="h-4 w-4" aria-hidden="true" />
                </div>

                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-start justify-between gap-2">
                    <div class="flex flex-wrap items-center gap-2">
                      <span
                        :class="[
                          'notifications-page-item__badge',
                          isDonationNotification(notification)
                            ? 'notifications-page-item__badge--donation'
                            : isTaskNotification(notification)
                            ? 'notifications-page-item__badge--task'
                            : notification.readStatus
                            ? 'notifications-page-item__badge--read'
                            : 'notifications-page-item__badge--unread',
                        ]"
                      >
                        {{ notificationTypeLabel(notification) }}
                      </span>
                      <span
                        v-if="!notification.readStatus"
                        class="notifications-page-item__badge notifications-page-item__badge--unread"
                      >
                        New
                      </span>
                    </div>
                    <time class="shrink-0 text-xs text-gray-400">
                      {{ formatTimestamp(notification.datetimeCreated) }}
                    </time>
                  </div>

                  <p class="mt-2 text-sm font-medium text-gray-900">
                    {{ formatNotificationMessage(notification) }}
                  </p>

                  <div
                    v-if="isDonationNotification(notification)"
                    class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500"
                  >
                    <span>
                      Amount: ${{
                        notification.extra?.donationAmount || notification.extra?.amountOfDonation
                      }}
                    </span>
                    <span>
                      Frequency:
                      {{
                        notification.extra?.donationFrequency ||
                        notification.extra?.frequencyOfDonation
                      }}
                    </span>
                  </div>

                  <p
                    v-if="
                      notification.notificationType === 'task_assigned' &&
                      notification.extra?.taskDescription
                    "
                    class="mt-2 text-xs text-gray-500"
                  >
                    {{ notification.extra.taskDescription }}
                  </p>

                  <p
                    v-if="notification.notificationType === 'task_completed'"
                    class="mt-2 text-xs text-gray-500"
                  >
                    Task: {{ notification.extra?.taskName || 'Untitled Task' }}
                  </p>

                  <p class="mt-2 text-xs text-gray-400">
                    {{ notification.readStatus ? 'Click to view' : 'Click to mark as read' }}
                  </p>
                </div>
              </div>
            </article>
          </div>
        </section>

        <BasePagination
          v-if="notifications?.list?.length"
          v-model="itemsPerPage"
          :page="notifications.pagination.page"
          :total-pages="totalPages"
          :total-count="notifications.pagination.totalCount"
          :display-start="displayStart"
          :display-end="displayEnd"
          :has-prev-page="notifications.pagination.hasPrevPage"
          :has-next-page="notifications.pagination.hasNextPage"
          :page-size-options="[10, 25, 50, 100]"
          @page-size-change="updateItemsPerPage"
          @prev="prevPage"
          @next="nextPage"
        />
      </div>
    </template>
  </DashboardContainer>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, triggerRef } from 'vue'
import { useStore } from 'vuex'
import {
  ArrowPathIcon,
  BellIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  InboxIcon,
} from '@heroicons/vue/24/outline'
import { ClipboardDocumentListIcon } from '@heroicons/vue/20/solid'
import { notificationFunctions } from '@/services/notifications'
import DashboardContainer from '@/components/DashboardContainer.vue'
import GivarooSpinner from '@/components/GivarooSpinner.vue'

export default {
  name: 'NotificationsList',
  components: {
    BellIcon,
    CheckCircleIcon,
    InboxIcon,
    CurrencyDollarIcon,
    ClipboardDocumentListIcon,
    DashboardContainer,
    GivarooSpinner,
    ArrowPathIcon,
  },
  setup() {
    const store = useStore()
    const { api, loadNotificationsOnce } = notificationFunctions()
    const notifications = ref(null)
    const loading = ref(false)
    const refreshing = ref(false)
    const error = ref(null)
    const itemsPerPage = ref(25)
    const totalPages = ref()
    const activeTab = ref('all')

    const isAuthenticated = computed(() => store.getters.isLoggedIn)
    const isFundraiser = computed(() => store.getters.isFundraiser)
    const isCanvassingOrgAdmin = computed(() => store.getters.isCanvassingOrgAdmin)
    const isCanvassingOrgFieldDirector = computed(() => store.getters.isCanvassingOrgFieldDirector)
    const isGivarooStaff = computed(() => store.getters.isGivarooStaff)

    const canViewNotifications = computed(() => {
      return (
        isFundraiser.value ||
        isCanvassingOrgAdmin.value ||
        isCanvassingOrgFieldDirector.value ||
        isGivarooStaff.value
      )
    })

    const list = computed(() => notifications.value?.list || [])
    const listCount = computed(() => list.value.length)
    const totalCount = computed(() => notifications.value?.pagination?.totalCount || 0)
    const unreadCount = computed(() => list.value.filter((n) => !n.readStatus).length)
    const readCount = computed(() => list.value.filter((n) => n.readStatus).length)
    const donationCount = computed(() => list.value.filter((n) => isDonationNotification(n)).length)
    const taskCount = computed(() => list.value.filter((n) => isTaskNotification(n)).length)

    const displayedNotifications = computed(() => {
      if (activeTab.value === 'unread') {
        return list.value.filter((n) => !n.readStatus)
      }
      if (activeTab.value === 'read') {
        return list.value.filter((n) => n.readStatus)
      }
      return list.value
    })

    const emptyTabMessage = computed(() => {
      if (activeTab.value === 'unread') return 'No unread notifications on this page.'
      if (activeTab.value === 'read') return 'No read notifications on this page.'
      return 'No notifications found.'
    })

    const displayStart = computed(() => {
      return (notifications.value.pagination.page - 1) * itemsPerPage.value + 1
    })

    const displayEnd = computed(() => {
      const end = notifications.value.pagination.page * itemsPerPage.value
      return end > notifications.value.pagination.totalCount
        ? notifications.value.pagination.totalCount
        : end
    })

    function isDonationNotification(notification) {
      return (
        notification.notificationType === 'qr_code_donation_received' ||
        notification.notificationType === 'donation_verified_by_donor'
      )
    }

    function isTaskNotification(notification) {
      return (
        notification.notificationType === 'task_assigned' ||
        notification.notificationType === 'task_completed'
      )
    }

    function notificationTypeLabel(notification) {
      if (notification.notificationType === 'qr_code_donation_received') return 'Donation'
      if (notification.notificationType === 'donation_verified_by_donor') return 'Verified'
      if (notification.notificationType === 'task_assigned') return 'Task assigned'
      if (notification.notificationType === 'task_completed') return 'Task completed'
      return 'Notification'
    }

    const updateItemsPerPage = async () => {
      loading.value = true
      notifications.value.pagination.size = itemsPerPage.value
      notifications.value.pagination.page = 1
      await notifications.value.refresh().catch((err) => {
        console.error('Error updating items per page:', err)
      })
      triggerRef(notifications)
      totalPages.value = Math.ceil(notifications.value.pagination.totalCount / itemsPerPage.value)
      loading.value = false
    }

    const nextPage = async () => {
      if (notifications.value.pagination.hasNextPage) {
        loading.value = true
        notifications.value.pagination.size = itemsPerPage.value
        await notifications.value.nextPage().catch((err) => {
          console.error('Error going to next page:', err)
        })
        triggerRef(notifications)
        totalPages.value = Math.ceil(notifications.value.pagination.totalCount / itemsPerPage.value)
        loading.value = false
      }
    }

    const prevPage = async () => {
      if (notifications.value.pagination.hasPrevPage) {
        loading.value = true
        notifications.value.pagination.size = itemsPerPage.value
        await notifications.value.prevPage().catch((err) => {
          console.error('Error going to previous page:', err)
        })
        triggerRef(notifications)
        totalPages.value = Math.ceil(notifications.value.pagination.totalCount / itemsPerPage.value)
        loading.value = false
      }
    }

    const fetchNotifications = async () => {
      if (!isAuthenticated.value || !canViewNotifications.value) return

      try {
        loading.value = true
        error.value = null
        notifications.value = await loadNotificationsOnce()
        totalPages.value = Math.ceil(notifications.value.pagination.totalCount / itemsPerPage.value)
      } catch (err) {
        error.value = err
        console.error('Failed to fetch notifications:', err)
      } finally {
        loading.value = false
      }
    }

    const refreshNotifications = async () => {
      if (!isAuthenticated.value || !canViewNotifications.value) return

      try {
        refreshing.value = true
        if (!notifications.value) {
          await fetchNotifications()
          return
        }
        await notifications.value.refresh()
        triggerRef(notifications)
        totalPages.value = Math.ceil(notifications.value.pagination.totalCount / itemsPerPage.value)
      } catch (err) {
        console.error('Failed to refresh notifications:', err)
      } finally {
        refreshing.value = false
      }
    }

    const toggleReadStatus = async (notification) => {
      try {
        if (!notification.readStatus) {
          await api.csc.markAsRead({
            id: notification.id,
            readStatus: true,
          })
          notification.readStatus = true
          window.dispatchEvent(
            new CustomEvent('notification-read', {
              detail: { notificationId: notification.id },
            }),
          )
        }
      } catch (err) {
        console.error('Failed to update notification status:', err)
      }
    }

    const handleNewNotification = () => {
      refreshNotifications()
    }

    const handleNotificationRead = () => {
      refreshNotifications()
    }

    const formatNotificationMessage = (notification) => {
      if (notification.notificationType === 'qr_code_donation_received') {
        const { donorFullName, campaignName } = notification.extra || {}
        return `${
          donorFullName || 'Someone'
        } has successfully submitted their donation for ${campaignName} campaign!`
      }
      if (notification.notificationType === 'donation_verified_by_donor') {
        const { donorName, campaignName } = notification.extra || {}
        return `${
          donorName || 'Someone'
        } has successfully verified their donation for ${campaignName} campaign!`
      }
      if (notification.notificationType === 'task_assigned') {
        const { taskName, assignerName } = notification.extra || {}
        return `${assignerName || 'Someone'} assigned you a new task: ${
          taskName || 'Untitled Task'
        }`
      }
      if (notification.notificationType === 'task_completed') {
        const { taskName, assigneeName } = notification.extra || {}
        return `${assigneeName || 'Someone'} has completed the task: ${taskName || 'Untitled Task'}`
      }
      return 'New notification'
    }

    const formatTimestamp = (timestamp) => {
      const date = new Date(timestamp)
      const now = new Date()
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))

      if (diffInHours < 1) {
        return 'Just now'
      } else if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
      } else {
        const diffInDays = Math.floor(diffInHours / 24)
        if (diffInDays < 7) {
          return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
        } else {
          return date.toLocaleDateString()
        }
      }
    }

    onMounted(() => {
      if (isAuthenticated.value && canViewNotifications.value) {
        fetchNotifications()
        window.addEventListener('new-notification', handleNewNotification)
        window.addEventListener('notification-read', handleNotificationRead)
      }
    })

    onUnmounted(() => {
      window.removeEventListener('new-notification', handleNewNotification)
      window.removeEventListener('notification-read', handleNotificationRead)
    })

    return {
      notifications,
      loading,
      refreshing,
      activeTab,
      displayedNotifications,
      emptyTabMessage,
      listCount,
      totalCount,
      unreadCount,
      readCount,
      donationCount,
      taskCount,
      itemsPerPage,
      totalPages,
      displayStart,
      displayEnd,
      updateItemsPerPage,
      nextPage,
      prevPage,
      fetchNotifications,
      refreshNotifications,
      toggleReadStatus,
      formatNotificationMessage,
      formatTimestamp,
      isDonationNotification,
      isTaskNotification,
      notificationTypeLabel,
      ArrowPathIcon,
    }
  },
}
</script>
